"use client";

import { useEffect, useState, useCallback } from "react";
import { format, parseISO } from "date-fns";
import { Calendar, Clock, Search, Trash2, CheckCircle, XCircle, Loader2, RefreshCw, SlidersHorizontal } from "lucide-react";
import { API_ENDPOINTS } from "@/constants/config";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import toast from "react-hot-toast";

const STATUS_META = {
  pending:   { label: "Pending",   cls: "bg-amber-100 text-amber-700" },
  confirmed: { label: "Confirmed", cls: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-700" },
};

function formatSlot(slot) {
  if (!slot) return "";
  const [h, m] = slot.split(":").map(Number);
  const suffix = h < 12 ? "AM" : "PM";
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, "0")} ${suffix}`;
}

function authHeader() {
  return { Authorization: `Bearer ${localStorage.getItem("token")}`, "Content-Type": "application/json" };
}

export default function DemoBookings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [updatingId, setUpdatingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [statsMap, setStatsMap] = useState({ total: 0, pending: 0, confirmed: 0, cancelled: 0 });

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== "all") params.set("status", status);
      if (q.trim()) params.set("q", q.trim());
      const url = `${API_ENDPOINTS.superadminDemoBookings}?${params}`;
      const res = await fetch(url, { headers: authHeader() });
      if (!res.ok) throw new Error("Failed to fetch");
      const json = await res.json();
      const bookings = json?.data?.bookings || [];
      setItems(bookings);

      // Compute stats from full unfiltered list only when no filter applied
      if (status === "all" && !q.trim()) {
        const sm = { total: json.total || bookings.length, pending: 0, confirmed: 0, cancelled: 0 };
        bookings.forEach((b) => { if (sm[b.status] !== undefined) sm[b.status]++; });
        setStatsMap(sm);
      }
    } catch (e) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [status, q]);

  useEffect(() => { fetchBookings(); }, [status]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchBookings();
  };

  const updateStatus = async (id, newStatus) => {
    setUpdatingId(id);
    try {
      const res = await fetch(API_ENDPOINTS.superadminDemoBooking(id), {
        method: "PATCH",
        headers: authHeader(),
        body: JSON.stringify({ status: newStatus }),
      });
      const json = await res.json();
      if (json.status === "success") {
        setItems((prev) => prev.map((b) => b._id === id ? { ...b, status: newStatus } : b));
        toast.success(`Marked as ${newStatus}`);
      } else {
        toast.error(json.message || "Update failed");
      }
    } catch {
      toast.error("Update failed");
    } finally {
      setUpdatingId(null);
    }
  };

  const deleteBooking = async (id) => {
    if (!confirm("Delete this booking?")) return;
    setDeletingId(id);
    try {
      const res = await fetch(API_ENDPOINTS.superadminDemoBooking(id), {
        method: "DELETE",
        headers: authHeader(),
      });
      const json = await res.json();
      if (json.status === "success") {
        setItems((prev) => prev.filter((b) => b._id !== id));
        toast.success("Booking deleted");
      } else {
        toast.error(json.message || "Delete failed");
      }
    } catch {
      toast.error("Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  const statCards = [
    { label: "Total", value: statsMap.total, color: "blue" },
    { label: "Pending", value: statsMap.pending, color: "amber" },
    { label: "Confirmed", value: statsMap.confirmed, color: "green" },
    { label: "Cancelled", value: statsMap.cancelled, color: "red" },
  ];

  const colorMap = {
    blue: "text-blue-600 bg-blue-500/10",
    amber: "text-amber-600 bg-amber-500/10",
    green: "text-green-600 bg-green-500/10",
    red: "text-red-600 bg-red-500/10",
  };

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-gray-900">Demo Bookings</h1>
        <p className="text-sm text-gray-500 mt-1">All scheduled demo requests from the website</p>
      </header>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {statCards.map(({ label, value, color }) => (
          <Card key={label} className="rounded-2xl border-gray-100 shadow-sm">
            <CardContent className="p-4">
              <p className="text-sm text-gray-500 font-medium">{label}</p>
              <p className={`text-3xl font-bold mt-1 ${colorMap[color].split(" ")[0]}`}>{value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="rounded-2xl border-gray-100 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <form onSubmit={handleSearch} className="flex gap-2 flex-1">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name, phone, school…"
                  className="pl-9 rounded-xl border-gray-200"
                />
              </div>
              <Button type="submit" variant="outline" className="rounded-xl border-gray-200">
                Search
              </Button>
            </form>

            <div className="flex gap-2 flex-wrap">
              {["all", "pending", "confirmed", "cancelled"].map((s) => (
                <button
                  key={s}
                  onClick={() => setStatus(s)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-all border ${
                    status === s
                      ? "bg-blue-600 text-white border-blue-600"
                      : "border-gray-200 text-gray-600 hover:border-blue-200 hover:text-blue-600"
                  }`}
                >
                  {s === "all" ? "All" : s}
                </button>
              ))}
            </div>

            <Button variant="outline" className="rounded-xl border-gray-200 gap-2" onClick={fetchBookings}>
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="rounded-2xl border-gray-100 shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <Calendar className="w-10 h-10 mb-2 opacity-40" />
              <p className="text-sm">No bookings found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10 bg-gray-50">
                  <tr>
                    {["Name / School", "Contact", "Date & Time", "Org Type", "Students", "Features", "Status", "Actions"].map((h) => (
                      <th key={h} className="px-5 py-3.5 text-left text-xs font-semibold uppercase tracking-wider text-gray-500 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {items.map((b) => (
                    <>
                      <tr key={b._id} className="border-t border-gray-100 hover:bg-gray-50/50 transition-colors">
                        <td className="px-5 py-3.5">
                          <p className="font-medium text-gray-900">{b.name}</p>
                          <p className="text-xs text-gray-500 mt-0.5">{b.schoolName}{b.city ? ` · ${b.city}` : ""}</p>
                          <p className="text-xs text-gray-400 mt-1">{b.designation}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <p className="text-gray-700">{b.phone}</p>
                          <p className="text-xs text-gray-400 mt-0.5">{b.email}</p>
                        </td>
                        <td className="px-5 py-3.5 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-gray-700">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            {format(parseISO(b.date), "MMM d")}
                          </div>
                          <div className="flex items-center gap-1.5 text-xs text-gray-500 mt-0.5">
                            <Clock className="w-3 h-3" />
                            {formatSlot(b.timeSlot)}
                          </div>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-gray-700">{b.organizationType}</td>
                        <td className="px-5 py-3.5 text-sm text-gray-700">{b.numberOfStudents}</td>
                        <td className="px-5 py-3.5">
                          <div className="flex flex-wrap gap-1">
                            {b.featuresToManage?.slice(0, 2).map((f) => (
                              <span key={f} className="inline-flex text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {f}
                              </span>
                            ))}
                            {b.featuresToManage?.length > 2 && (
                              <span className="text-xs text-gray-500">+{b.featuresToManage.length - 2}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_META[b.status]?.cls}`}>
                            {STATUS_META[b.status]?.label}
                          </span>
                        </td>
                        <td className="px-5 py-3.5">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => setExpandedId(expandedId === b._id ? null : b._id)}
                              className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50 transition-colors"
                              title="View Details"
                            >
                              <SlidersHorizontal className="w-4 h-4" />
                            </button>
                            {b.status !== "confirmed" && (
                              <button
                                onClick={() => updateStatus(b._id, "confirmed")}
                                disabled={updatingId === b._id}
                                title="Confirm"
                                className="p-1.5 rounded-lg text-green-600 hover:bg-green-50 disabled:opacity-50 transition-colors"
                              >
                                {updatingId === b._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                              </button>
                            )}
                            {b.status !== "cancelled" && (
                              <button
                                onClick={() => updateStatus(b._id, "cancelled")}
                                disabled={updatingId === b._id}
                                title="Cancel"
                                className="p-1.5 rounded-lg text-amber-600 hover:bg-amber-50 disabled:opacity-50 transition-colors"
                              >
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteBooking(b._id)}
                              disabled={deletingId === b._id}
                              title="Delete"
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50 transition-colors"
                            >
                              {deletingId === b._id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandedId === b._id && (
                        <tr className="border-t border-gray-100 bg-gray-50">
                          <td colSpan="8" className="px-5 py-4">
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="font-medium text-gray-600">Designation</p>
                                <p className="text-gray-900 mt-1">{b.designation}</p>
                              </div>
                              <div>
                                <p className="font-medium text-gray-600">Has School Buses</p>
                                <p className="text-gray-900 mt-1">{b.hasSchoolBuses ? "Yes" : "No"}</p>
                              </div>
                              {b.hasSchoolBuses && (
                                <div>
                                  <p className="font-medium text-gray-600">Number of Buses</p>
                                  <p className="text-gray-900 mt-1">{b.numberOfBuses}</p>
                                </div>
                              )}
                              <div className="md:col-span-3">
                                <p className="font-medium text-gray-600">Features to Manage</p>
                                <div className="flex flex-wrap gap-2 mt-2">
                                  {b.featuresToManage?.map((f) => (
                                    <span key={f} className="inline-flex bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-medium">
                                      {f}
                                    </span>
                                  ))}
                                </div>
                              </div>
                              {b.specificRequirements && (
                                <div className="md:col-span-3">
                                  <p className="font-medium text-gray-600">Specific Requirements</p>
                                  <p className="text-gray-900 mt-1 whitespace-pre-wrap">{b.specificRequirements}</p>
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-gray-600">Booked On</p>
                                <p className="text-gray-900 mt-1 text-xs">{format(new Date(b.createdAt), "MMM d, yyyy HH:mm")}</p>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
